import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import { useI18n } from '../i18n';
import { useCan, useMe } from '../hooks/useMe';
import api from '../services/api';

// Los colores del punto vienen del servidor, para que la interfaz no tenga que
// saber qué significa cada tipo de entrada.
const DOT = {
  mauve: '#847782',
  coral: '#FF6666',
  plum: '#531253',
  bad: '#AE5B61',
  ok: '#4F7A5F',
};

export default function AccountTimeline({ tenantId, source, onChanged }) {
  const { t, fmtDate } = useI18n();
  const canLog = useCan('log_activity');
  const { me } = useMe();

  const [entries, setEntries] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [composing, setComposing] = useState(false);

  const load = useCallback(() => {
    api.get(`/api/accounts/${tenantId}/timeline`, { params: { source } })
      .then((res) => setEntries(res.data))
      .catch(() => setError(t('timeline.loadError')));
  }, [tenantId, source, t]);

  useEffect(() => { load(); }, [load]);

  async function addNote() {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await api.post(`/api/accounts/${tenantId}/notes`, { body: draft.trim() }, { params: { source } });
      setDraft('');
      setComposing(false);
      load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || t('timeline.saveError'));
    } finally {
      setSaving(false);
    }
  }

  // Sólo se borran notas: una sesión, un cambio de campo o una cotización no
  // son entradas sueltas del historial, son hechos de otra tabla.
  async function removeNote(entry) {
    if (!window.confirm(t('timeline.deleteConfirm'))) return;
    setSaving(true);
    try {
      await api.delete(`/api/notes/${entry.ref_id}`);
      load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || t('timeline.deleteError'));
    } finally {
      setSaving(false);
    }
  }

  const canDeleteNote = (e) =>
    e.type === 'note' && canLog && (me?.app_role === 'admin' || e.actor === me?.display_name);

  const describe = (e) => {
    if (e.type === 'event') {
      return (
        <>
          {e.title}{' '}
          <Box component="span" sx={{ color: '#847782' }}>{e.from}</Box>
          {' → '}
          <Box component="span" sx={{ fontWeight: 500 }}>{e.to}</Box>
        </>
      );
    }
    if (e.type === 'quote') {
      return (
        <>
          <Box component="span" sx={{ fontWeight: 500 }}>{e.title}</Box>
          {e.body ? ` · ${e.body}` : ''}
        </>
      );
    }
    if (e.type === 'session') {
      return (
        <>
          <Box component="span" sx={{ fontWeight: 500 }}>{e.title}</Box>
          {e.body ? ` — ${e.body}` : ''}
        </>
      );
    }
    return e.body;
  };

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 1.6,
        fontSize: 12, fontWeight: 600, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: '#847782',
      }}>
        {t('timeline.title')}
        <Box sx={{ flexGrow: 1 }} />
        {canLog && !composing && (
          <Button size="small" onClick={() => setComposing(true)}
            sx={{ fontSize: 11.5, color: '#FF6666', textTransform: 'none', letterSpacing: 0 }}>
            {t('timeline.addNote')}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {composing && (
        <Box sx={{ mb: 2.4 }}>
          <TextField
            fullWidth multiline minRows={3} autoFocus size="small"
            placeholder={t('timeline.notePlaceholder')}
            value={draft} disabled={saving}
            onChange={(e) => setDraft(e.target.value)}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button size="small" variant="contained" disabled={saving || !draft.trim()} onClick={addNote}>
              {t('common.save')}
            </Button>
            <Button size="small" onClick={() => { setComposing(false); setDraft(''); }}
              sx={{ color: '#847782' }}>{t('common.close')}</Button>
          </Box>
        </Box>
      )}

      {!entries && <Skeleton variant="rectangular" height={160} />}

      {entries && entries.length === 0 && (
        <Typography sx={{ fontSize: 13.5, color: '#847782' }}>{t('timeline.empty')}</Typography>
      )}

      {entries && entries.map((e, i) => (
        <Box key={e.id} sx={{
          display: 'grid', gridTemplateColumns: '15px 1fr', gap: 1.4,
          pb: i === entries.length - 1 ? 0 : 2, position: 'relative',
        }}>
          {/* El hilo vertical une las entradas; la última no lo lleva. */}
          {i !== entries.length - 1 && (
            <Box sx={{
              position: 'absolute', left: 7, top: 15, bottom: 0,
              width: '1px', backgroundColor: '#E4E0E2',
            }} />
          )}
          <Box sx={{
            width: 9, height: 9, borderRadius: '50%', mt: 0.7,
            backgroundColor: DOT[e.dot] || DOT.mauve, zIndex: 1,
          }} />
          <Box>
            <Typography sx={{ fontSize: 11, color: '#847782' }}>
              {fmtDate(e.at)}{e.actor ? ` · ${e.actor}` : ''}
              {e.edited ? ` · ${t('timeline.edited')}` : ''}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Typography sx={{ fontSize: 13.5, flexGrow: 1 }}>{describe(e)}</Typography>
              {canDeleteNote(e) && (
                <Box
                  component="button"
                  onClick={() => removeNote(e)}
                  disabled={saving}
                  title={t('common.delete')}
                  sx={{
                    border: 'none', background: 'none', cursor: 'pointer', p: 0,
                    color: '#847782', fontSize: 15, lineHeight: 1, fontFamily: 'inherit',
                    '&:hover': { color: '#AE5B61' },
                  }}
                >×</Box>
              )}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
